import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import http from '../Api/http';
import { GetDepartments, GetDesignations } from '../Api/Api';
import { selectActiveEntityId, ALL_ENTITY_ID } from '../redux/slices/orgSlice';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';

// An entity admin only receives their own entity's rows from the API, so the
// client-side entity filter is applied for Master Admin only (who gets every entity).
const pick = (settled, key, activeEntityId, scopeByEntity) => {
    const body = settled.status === 'fulfilled' ? settled.value?.data : null;
    if (!body || body.error) return { rows: [], names: [] };
    const all = Array.isArray(body?.data?.[key]) ? body.data[key] : [];
    const rows = (!scopeByEntity || activeEntityId === ALL_ENTITY_ID
        ? all
        : all.filter((r) => r.entityId == null || String(r.entityId) === String(activeEntityId))
    ).filter((r) => r.isActive !== false && String(r.status || 'Active').toLowerCase() !== 'inactive');
    const names = [...new Set(rows.map((r) => (r.name || '').trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
    return { rows, names };
};

// `quiet` is for roles that may not be allowed these lists: a denied call leaves the lists empty instead of
// bouncing the user to the dashboard.
export default function useOrgOptions({ quiet = false } = {}) {
    const activeEntityId = useSelector(selectActiveEntityId);
    const scopeByEntity = useSelector(selectUserTypeId) === USER_TYPE.MASTER_ADMIN;
    const [state, setState] = useState({
        departments: [], designations: [], departmentNames: [], designationNames: [], loading: true,
    });

    // After a refresh the active entity changes (seed -> real) and the lists are
    // fetched twice; only the latest response may update state, or a stale empty
    // one can land last and leave the dropdowns disabled.
    const reqId = useRef(0);

    const load = useCallback(async () => {
        const mine = ++reqId.current;
        setState((s) => ({ ...s, loading: true }));
        const [deptRes, desigRes] = await Promise.allSettled([
            http.get(GetDepartments, quiet ? { noModuleBounce: true } : undefined),
            http.get(GetDesignations, quiet ? { noModuleBounce: true } : undefined),
        ]);
        if (mine !== reqId.current) return;
        const dep = pick(deptRes, 'departments', activeEntityId, scopeByEntity);
        const des = pick(desigRes, 'designations', activeEntityId, scopeByEntity);
        setState({
            departments: dep.rows,
            designations: des.rows,
            departmentNames: dep.names,
            designationNames: des.names,
            loading: false,
        });
    }, [activeEntityId, scopeByEntity, quiet]);

    useEffect(() => { load(); }, [load]);

    return { ...state, reload: load };
}
